import React from 'react';
import {
  Button, Form,
} from 'antd';
import Input from 'components/components/Input';
import DataColumns from 'components/components/DataColumns';
import ServiceDocuments from 'components/RequestServiceDrawer/components/ServiceOfferedDocView/Documents';
import OfficeFields from 'components/RequestServiceDrawer/components/InputColumns/components/OfficeColumns/Fields';
import QuantityField, { getUnitQuantityText } from 'components/RequestServiceDrawer/components/InputColumns/QuantityField';
import ServiceRequestTotalContent from 'components/RequestServiceDrawer/components/ServiceRequestTotal/Content';
import { convertGetServiceResponse } from 'packages/service_repository';
import { ManagedStartupType } from 'packages/venture_builder_repository';
import { PortalOrder, PricedPortalService } from 'packages/venture_builder_repository/portal/types';
import { orderPrice, selectedStartup } from 'packages/venture_builder_repository/portal/utils';
import { getServiceRequestOverviewColumns } from 'components/RequestServiceDrawer/utils/constants';
import { money, startupLabel } from './shared';

type Props = {
  service: PricedPortalService,
  startups: ManagedStartupType[],
  initialStartupId?: number,
  pending: boolean,
  blocked?: boolean,
  onSubmit: (order: PortalOrder) => void,
  onCancel?: () => void,
};

const OrderForm = ({
  service, startups, initialStartupId, pending, blocked, onSubmit, onCancel,
}: Props) => {
  const [form] = Form.useForm<PortalOrder>();
  const quantity = Form.useWatch('quantity', form);
  const desks = Form.useWatch('desks', form);
  const details = convertGetServiceResponse({
    ...service, category: service.category || { id: service.categoryID || 0 },
  })!;
  const office = service.categoryID === 1;
  let total: string;
  try {
    total = money(orderPrice(service, { quantity, desks }));
  } catch {
    total = '—';
  }
  return (
    <Form
      className="vb-portal__order-form"
      form={form}
      layout="vertical"
      disabled={pending || blocked}
      initialValues={{ startupId: initialStartupId, quantity: 1, desks: 1 }}
      onFinish={(values) => onSubmit({ ...values, serviceId: service.id })}
    >
      <DataColumns columns={getServiceRequestOverviewColumns(details, true)} />
      <Input
        layout="vertical"
        label="Startup"
        name="startupId"
        required
        rules={[{
          validator: (_, value) => (selectedStartup(startups, value)
            ? Promise.resolve() : Promise.reject(new Error('Choose a startup from your portfolio'))),
        }]}
      >
        <Input.Select
          placeholder="Choose a startup"
          showSearch
          options={startups.map((item) => ({ value: item.id, label: startupLabel(item) }))}
        />
      </Input>
      {office ? (
        <OfficeFields precision={0} names={{ officeType: 'officeType', desks: 'desks', quantity: 'quantity' }} />
      ) : <QuantityField name="quantity" label={getUnitQuantityText(details)} precision={0} />}
      <ServiceDocuments details={details} />
      <ServiceRequestTotalContent>
        {total}
        <span className="vat-text">&nbsp;(ex. VAT)</span>
      </ServiceRequestTotalContent>
      <p>The service provider will confirm the final price in the quotation.</p>
      <div className="vb-portal__actions">
        {onCancel && <Button className="secondary-button" disabled={pending} onClick={onCancel}>Discard</Button>}
        <Button htmlType="submit" className="primary-button" loading={pending} disabled={blocked}>Add to requests</Button>
      </div>
    </Form>
  );
};

OrderForm.defaultProps = { initialStartupId: undefined, blocked: false, onCancel: undefined };

export default OrderForm;
